import { Component } from '@theme/component';

/**
 * The cart total as the server rendered it, read out of any one of the section
 * payloads that came back with a cart update.
 *
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
function countFromSections(sections) {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
    if (!el) continue;
    const n = parseInt(el.textContent ?? '', 10);
    if (Number.isFinite(n)) return n;
  }
  return null;
}
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    // Deliberately nothing on a restore from the back-forward cache.
    //
    // `ensureCartBubbleIsCorrect` reconciles against sessionStorage, which this
    // same tab wrote the last time it rendered the bubble. A restored document
    // carries a stale DOM and an equally stale sessionStorage, so that check
    // can only ever agree with the wrong number, and its ten second freshness
    // window has long expired by the time anyone comes back from a checkout.
    //
    // `mab-cart-refresh.js` handles the restore by asking Shopify, and the
    // answer reaches this component as an ordinary cart update. Running the
    // sessionStorage check here as well would paint a stale count first and
    // have it corrected a moment later, which is a flicker on the one number a
    // buyer is watching.
    if (event.persisted) return;
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const data = event.detail.data ?? {};

    // Prefer the count Shopify just rendered. An add dispatches `itemCount` as
    // the quantity it added, and the bubble then adds that to the number
    // already on screen, which is arithmetic on top of whatever the page
    // happened to be showing. If that number was stale the answer is stale too,
    // and it stays stale until something else corrects it. The section markup
    // in the same answer carries the real total, so read that instead whenever
    // it is there.
    const fromServer = countFromSections(data.sections);
    if (fromServer !== null) {
      this.renderCartBubble(fromServer, false);
      return;
    }

    const itemCount = data.itemCount ?? 0;
    const comingFromProductForm = data.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
